import { type Environment, fetchQuery, graphql } from 'react-relay';
import type { CanonicalId } from '@atlassian/jira-common-constants/src/project-types';
import { NOT_ENROLLED, VARIATION, CONTROL } from '@atlassian/jira-common-util-get-experiment-group';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import type {
	canProductUserPayQuery$variables,
	canProductUserPayQuery,
} from '@atlassian/jira-relay/src/__generated__/canProductUserPayQuery.graphql';
import { PRODUCT_DISCOVERY } from '@atlassian/jira-shared-types';

export const Query = graphql`
	query canProductUserPayQuery($cloudId: ID!, $productKey: String!) {
		tenantContexts(cloudIds: [$cloudId])
			@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
			entitlementInfo(hamsProductKey: $productKey) {
				entitlement {
					subscription {
						accountDetails {
							invoiceGroup {
								experienceCapabilities {
									configurePaymentV2 {
										isAvailableToUser @required(action: THROW)
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

type CanProductUserPayResult = {
	canPay: boolean | null;
	cohort: typeof NOT_ENROLLED | typeof VARIATION | typeof CONTROL;
};

const getExperimentCohort = (experimentCohort: string) => {
	switch (experimentCohort) {
		case VARIATION:
			return VARIATION;
		case CONTROL:
			return CONTROL;
		default:
			return NOT_ENROLLED;
	}
};

export const canProductUserPay = async (
	cloudId: string,
	productKey: CanonicalId,
	relayEnvironment: Environment,
	onExperimentExposure: (cohort: string) => void,
): Promise<CanProductUserPayResult> => {
	const [expConfig, fireExperimentExposure] = UNSAFE_noExposureExp(
		'more_user_products_can_upgrade_and_pay_in_product',
	);
	const experimentCohort = expConfig.get('cohort', NOT_ENROLLED);
	if (experimentCohort !== NOT_ENROLLED) {
		// JPD product key as used by Commerce does not include .ondemand
		const commerceProductKey =
			productKey === PRODUCT_DISCOVERY ? productKey : `${productKey}.ondemand`;

		const variables: canProductUserPayQuery$variables = {
			cloudId,
			productKey: commerceProductKey,
		};
		const response = await fetchQuery<canProductUserPayQuery>(relayEnvironment, Query, variables, {
			fetchPolicy: 'store-or-network',
		}).toPromise();

		const entitlementInfo =
			response?.tenantContexts && response.tenantContexts.length > 0
				? response.tenantContexts[0]?.entitlementInfo
				: undefined;

		const canPay =
			entitlementInfo?.entitlement?.subscription?.accountDetails?.invoiceGroup
				?.experienceCapabilities?.configurePaymentV2?.isAvailableToUser;

		if (canPay !== undefined) {
			if (canPay) {
				// User is eligible since they can pay, so we fire the exposure event
				fireExperimentExposure();
				onExperimentExposure(experimentCohort);
			}

			// Handles the case when canPay is successfully evaluated to true or false
			return {
				canPay,
				cohort: getExperimentCohort(experimentCohort),
			};
		}
	}

	// If user is not enrolled OR canPay isn't evaluated successfully, we should return null
	return {
		canPay: null,
		cohort: NOT_ENROLLED,
	};
};
